import { Fade, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import useShowSnackbar from 'hooks/useShowSnackbar'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import { useFetchNotesQuery, useUpdateNoteMutation } from 'store/modules/PatientNotes'
import { trackEvent } from 'utils/analyticsUtils'
import CreatorInfo from './CreatorInfo'
import DeleteNoteModal from './DeleteNoteModal'
import NoteContent from './NoteContent'
import NoteItemActions from './NoteItemActions'
import NeedsAttentionNoteActions from './NeedsAttentionNoteActions'
import { PinNoteIcon } from 'components/common/icons'
import {
  NoteTypes,
  UserNoteVisiblities,
  findOpenNeedsAttentionTag,
  isLiveNeedsAttentionNote,
  replaceNeedsAttentionNoteText
} from 'logic/patientNotesLogic'
import { createIdForTest } from 'utils/testingUtils.js'

const useStyles = makeStyles(theme => ({
  noteItemRoot: {
    position: 'relative',
    width: '100%',
    backgroundColor: ({ isPublic }) => (isPublic ? '#F3F3F4' : 'rgba(235, 238, 255, 1)'),
    borderRadius: 10,
    padding: 8,
    marginBottom: 12,
    transition: 'background-color 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: ({ isPublic }) => (isPublic ? '#e5e5e5' : '#E1E4FA')
    }
  }
}))

const NoteItem = React.forwardRef(
  (
    {
      id,
      body,
      attachments,
      patientGrinUserId,
      isPinned,
      metadata,
      createdAt,
      visibility = UserNoteVisiblities.Public,
      withPinIndication = true,
      needsAttentionActions = null
    },
    ref
  ) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const doctor = useSelector(state => state.profileReducer.doctor)
    const patientId = useSelector(state => state.patientsReducer.patient?.id)
    // The open Needs Attention is the selected patient's. While the next patient's notes load, the list
    // still shows the previous patient's notes, so their tag must not count for this note.
    const openNeedsAttentionTag = useSelector(state =>
      state.patientsReducer.patient?.user?.id === patientGrinUserId
        ? findOpenNeedsAttentionTag(state.patientsReducer.patient?.patientTags?.items)
        : undefined
    )

    const [isHovered, setIsHovered] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentBody, setCurrentBody] = useState()
    const [currentHINote, setCurrentHINote] = useState()
    const [isDeleteNoteModalOpen, setIsDeleteModalOpen] = useState(false)

    const parsedMetadata = JSON.parse(metadata || '{}')

    const isOwnedByCurrentUser = useMemo(
      () => doctor.user.id === parsedMetadata.creatorUserId,
      [doctor, parsedMetadata]
    )
    const isNeedsAttentionNote = useMemo(
      () =>
        parsedMetadata.noteType === NoteTypes.NeedsAttention ||
        parsedMetadata.noteType === NoteTypes.NeedsAttentionResolved,
      [parsedMetadata]
    )
    const isOpenNeedsAttentionNote = parsedMetadata.noteType === NoteTypes.NeedsAttention
    // Reads the panel's cached notes list (no extra request) to tell the live request from past ones.
    const { isLive: isLiveNeedsAttention } = useFetchNotesQuery(
      { patientGrinUserId },
      {
        skip: !isOpenNeedsAttentionNote || !patientGrinUserId,
        selectFromResult: ({ data }) => ({
          isLive: isLiveNeedsAttentionNote({ noteId: id, notes: data, hasOpenNeedsAttention: !!openNeedsAttentionTag })
        })
      }
    )
    // GR-10746: the creator may edit a regular note, or the Needs Attention note behind the patient's
    // open Needs Attention. Past (resolved) requests and resolved notes stay as written.
    const isEditable = useMemo(() => {
      if (!isOwnedByCurrentUser || parsedMetadata.noteType === NoteTypes.NeedsAttentionResolved) {
        return false
      }
      return isOpenNeedsAttentionNote ? isLiveNeedsAttention : true
    }, [isOwnedByCurrentUser, parsedMetadata.noteType, isOpenNeedsAttentionNote, isLiveNeedsAttention])

    // Resolving the Needs Attention mid-edit makes the note a past request: drop the edit, don't save it.
    useEffect(() => {
      if (isEditMode && !isEditable) {
        setIsEditMode(false)
      }
    }, [isEditMode, isEditable])

    const shouldDisplayActions = useMemo(() => isHovered && !isEditMode, [isEditMode, isHovered])

    const [updateNote, { isError: isFailedUpdatingNote }] = useUpdateNoteMutation()
    useShowSnackbar({
      isOpen: isFailedUpdatingNote,
      message: t('pages.patients.patientSwitchableView.notes.generalError')
    })

    const handleToggleEditMode = useCallback(() => {
      trackEvent('Patient notes - Edit mode toggled', {
        toggle: !isEditMode ? 'off' : 'true'
      })
      setIsEditMode(!isEditMode)
    }, [isEditMode])

    const handleBodySaved = useCallback(async () => {
      trackEvent('Patient notes - New Body saved', { patientGrinUserId, isNeedsAttentionNote })
      setIsEditMode(!isEditMode)

      // The panel shows a Needs Attention note's metadata.hiNote, so that is what gets edited; its body follows.
      const storedNote = parsedMetadata.hiNote || ''
      const previousNote = storedNote.trim()
      const note = (currentHINote || '').trim()
      // Captured before the await: the user may switch patient while the save is in flight.
      const needsAttentionTagId = openNeedsAttentionTag?.id
      const hasChanged = isOpenNeedsAttentionNote ? note !== previousNote : currentBody !== body
      if (!hasChanged) {
        return
      }

      const result = await updateNote({
        id,
        variables: {
          body: isOpenNeedsAttentionNote
            ? replaceNeedsAttentionNoteText({ body, previousNote: storedNote, note })
            : currentBody,
          metadata: JSON.stringify({
            ...parsedMetadata,
            ...(isOpenNeedsAttentionNote && { hiNote: note }),
            lastEditedAt: new Date().toISOString()
          }),
          grinUserId: patientGrinUserId
        }
      })

      if (isOpenNeedsAttentionNote && needsAttentionTagId && !result?.error) {
        dispatch(
          Actions.updateNeedsAttentionTagNote({ patientId, patientTagId: needsAttentionTagId, previousNote, note })
        )
      }
    }, [
      patientGrinUserId,
      isEditMode,
      updateNote,
      id,
      body,
      currentBody,
      parsedMetadata,
      currentHINote,
      isNeedsAttentionNote,
      isOpenNeedsAttentionNote,
      openNeedsAttentionTag,
      patientId,
      dispatch
    ])

    const handleUpdateVisibility = useCallback(
      async value => {
        trackEvent('Patient notes - Note visibility updated', { visibilityValue: value ?? 'public', patientGrinUserId })
        await updateNote({ id, variables: { visibility: value, grinUserId: patientGrinUserId } })
      },
      [patientGrinUserId, id, updateNote]
    )

    const handleBodyDiscarded = useCallback(() => {
      trackEvent('Patient notes - Body discarded')
      setCurrentBody(body)
      setCurrentHINote(parsedMetadata.hiNote)
      setIsEditMode(false)
    }, [body, parsedMetadata.hiNote])

    const handleDeleteNoteClicked = useCallback(() => {
      trackEvent('Patient notes - Delete note modal opened')
      setIsDeleteModalOpen(true)
    }, [])

    const handleTogglePinnedNote = useCallback(async () => {
      trackEvent('Patient note: Note pin toggled', {
        noteId: id,
        isPinned: !isPinned,
        source: 'Switchable view'
      })
      await updateNote({ id, variables: { isPinned: !isPinned, grinUserId: patientGrinUserId } })
    }, [patientGrinUserId, id, isPinned, updateNote])

    const handleCopyNoteId = useCallback(() => navigator.clipboard.writeText(id), [id])

    useEffect(() => {
      setCurrentBody(body)
    }, [body])

    // Synced like the body: re-filling whenever the text was empty undid clearing it while editing.
    useEffect(() => {
      setCurrentHINote(parsedMetadata.hiNote)
    }, [parsedMetadata.hiNote])

    const classes = useStyles({ isPublic: visibility === UserNoteVisiblities.Public })

    return (
      <>
        <Grid
          container
          direction="column"
          id={createIdForTest({ componentId: 'patient-note', title: id })}
          data-pinned={!!isPinned}
          className={classes.noteItemRoot}
          ref={ref}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Grid item>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item style={{ flex: 1 }}>
                <CreatorInfo
                  noteId={id}
                  userId={parsedMetadata.creatorUserId}
                  noteParsedMetadata={parsedMetadata}
                  createdAt={createdAt}
                  visibility={visibility}
                  onVisibilitySelected={handleUpdateVisibility}
                  isVisibilityEditable={isEditable && !isNeedsAttentionNote}
                />
              </Grid>
              {withPinIndication && isPinned && (
                <Grid item>
                  <PinNoteIcon color="#999" />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item style={{ width: '100%' }}>
            <NoteContent
              noteId={id}
              isEditMode={isEditMode}
              isNeedsAttentionNote={isOpenNeedsAttentionNote}
              currentHINote={currentHINote}
              setCurrentHINote={setCurrentHINote}
              body={currentBody}
              attachments={attachments}
              noteParsedMetadata={parsedMetadata}
              setBody={setCurrentBody}
              onBodySaved={handleBodySaved}
              onBodyDiscarded={handleBodyDiscarded}
              isHovered={isHovered}
            />
          </Grid>
          {needsAttentionActions && (
            <Grid item>
              <NeedsAttentionNoteActions
                canReply={needsAttentionActions.canReply}
                onReplyClicked={needsAttentionActions.onReplyClicked}
                onResolveClicked={needsAttentionActions.onResolveClicked}
                isSaving={needsAttentionActions.isSaving}
              />
            </Grid>
          )}
          <Fade in={shouldDisplayActions} timeout={500}>
            <Grid item>
              <NoteItemActions
                noteId={id}
                isEditable={isEditable}
                isDeletable={isOwnedByCurrentUser}
                isPinned={isPinned}
                onToggleEditMode={handleToggleEditMode}
                onDeleteNoteClicked={handleDeleteNoteClicked}
                onTogglePinnedNoteClicked={handleTogglePinnedNote}
                onCopyIdClicked={handleCopyNoteId}
              />
            </Grid>
          </Fade>
        </Grid>
        {isDeleteNoteModalOpen && (
          <DeleteNoteModal isOpen={isDeleteNoteModalOpen} setIsOpen={setIsDeleteModalOpen} noteId={id} />
        )}
      </>
    )
  }
)

export default NoteItem
